import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Database,
  FileJson,
  Image,
  FolderOpen,
  CheckCircle2,
  AlertTriangle,
  Folder,
  FileText,
  Info,
  Lightbulb,
  XCircle,
} from 'lucide-react';
import { MONAIDataset, MONAITaskType, MONAIModality, MONAIDatasetFormat } from '@/mock/gpu-instance';

interface MONAIDatasetCardProps {
  dataset: MONAIDataset | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectDataset?: (dataset: MONAIDataset) => void;
}

const getFormatIcon = (format: MONAIDatasetFormat) => {
  switch (format) {
    case 'nifti':
      return <FileJson className="h-4 w-4 text-blue-500" />;
    case 'dicom':
      return <FolderOpen className="h-4 w-4 text-purple-500" />;
    case 'png':
      return <Image className="h-4 w-4 text-green-500" />;
    case 'medmnist':
      return <Database className="h-4 w-4 text-orange-500" />;
  }
};

const getModalityColor = (modality: MONAIModality) => {
  const colors: Record<MONAIModality, string> = {
    CT: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
    MRI: 'bg-purple-500/10 text-purple-600 border-purple-500/30',
    'X-Ray': 'bg-cyan-500/10 text-cyan-600 border-cyan-500/30',
    Ultrasound: 'bg-green-500/10 text-green-600 border-green-500/30',
    Pathology: 'bg-pink-500/10 text-pink-600 border-pink-500/30',
    Fundus: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
    Dermoscopy: 'bg-red-500/10 text-red-600 border-red-500/30',
    Endoscopy: 'bg-teal-500/10 text-teal-600 border-teal-500/30',
  };
  return colors[modality];
};

// Get directory structure based on format
const getDirectoryStructure = (format: MONAIDatasetFormat, task: MONAITaskType): string[] => {
  switch (format) {
    case 'nifti':
      if (task === 'segmentation') {
        return [
          'dataset/',
          '├── imagesTr/',
          '│   ├── case_001.nii.gz',
          '│   ├── case_002.nii.gz',
          '│   └── ...',
          '├── labelsTr/',
          '│   ├── case_001.nii.gz',
          '│   ├── case_002.nii.gz',
          '│   └── ...',
          '├── imagesTs/',
          '│   └── ...',
          '└── dataset.json',
        ];
      }
      return [
        'dataset/',
        '├── images/',
        '│   ├── case_001.nii.gz',
        '│   ├── case_002.nii.gz',
        '│   └── ...',
        '├── labels.csv',
        '└── dataset.json',
      ];
    case 'dicom':
      return [
        'dataset/',
        '├── patient_001/',
        '│   ├── study_001/',
        '│   │   ├── series_001/',
        '│   │   │   ├── 00001.dcm',
        '│   │   │   ├── 00002.dcm',
        '│   │   │   └── ...',
        '│   │   └── series_002/',
        '│   │       └── ...',
        '│   └── study_002/',
        '│       └── ...',
        '├── patient_002/',
        '│   └── ...',
        '└── labels/',
        '    ├── patient_001.nii.gz',
        '    └── ...',
      ];
    case 'png':
      if (task === 'classification') {
        return [
          'dataset/',
          '├── train/',
          '│   ├── class_0/',
          '│   │   ├── image_001.png',
          '│   │   └── ...',
          '│   ├── class_1/',
          '│   │   └── ...',
          '│   └── class_2/',
          '│       └── ...',
          '├── val/',
          '│   └── (same structure)',
          '└── test/',
          '    └── (same structure)',
        ];
      }
      return [
        'dataset/',
        '├── images/',
        '│   ├── train/',
        '│   │   └── *.png',
        '│   └── val/',
        '│       └── *.png',
        '└── masks/',
        '    ├── train/',
        '    │   └── *.png',
        '    └── val/',
        '        └── *.png',
      ];
    case 'medmnist':
      return [
        'dataset/',
        '├── pathmnist.npz',
        '│   ├── train_images: (N, 28, 28, 3)',
        '│   ├── train_labels: (N,)',
        '│   ├── val_images: (M, 28, 28, 3)',
        '│   ├── val_labels: (M,)',
        '│   ├── test_images: (K, 28, 28, 3)',
        '│   └── test_labels: (K,)',
        '└── README.md',
      ];
  }
};

// Get annotation example based on format and task
const getAnnotationExample = (format: MONAIDatasetFormat, task: MONAITaskType): string => {
  switch (format) {
    case 'nifti':
      if (task === 'segmentation') {
        return `# dataset.json (Medical Decathlon format)
{
  "name": "Liver Tumor Segmentation",
  "description": "CT scans with liver and tumor labels",
  "tensorImageSize": "3D",
  "modality": { "0": "CT" },
  "labels": {
    "0": "background",
    "1": "liver",
    "2": "tumor"
  },
  "numTraining": 131,
  "numTest": 70,
  "training": [
    {
      "image": "./imagesTr/liver_0.nii.gz",
      "label": "./labelsTr/liver_0.nii.gz"
    }
  ],
  "test": ["./imagesTs/liver_131.nii.gz"]
}`;
      }
      return `# labels.csv
case_id,label
case_001,0
case_002,1
case_003,0
case_004,2`;

    case 'dicom':
      return `# DICOM tags used for labels (example)
# PatientID: patient_001
# StudyInstanceUID: 1.2.840...
# SeriesInstanceUID: 1.2.840...
# Modality: CT
# BodyPartExamined: CHEST

# Segmentation mask stored as NIfTI:
# labels/patient_001.nii.gz
# - Same dimensions as DICOM series
# - Values: 0=background, 1=organ, 2=lesion`;

    case 'png':
      if (task === 'classification') {
        return `# Folder structure defines labels
train/
├── malignant/     # Class 0
│   ├── img_001.png
│   └── img_002.png
├── benign/        # Class 1
│   └── img_003.png
└── normal/        # Class 2
    └── img_004.png

# Optional: labels.csv for additional metadata
filename,label,patient_id,age
img_001.png,malignant,P001,65
img_002.png,malignant,P002,72`;
      }
      return `# For segmentation: parallel mask images
images/train/img_001.png  →  masks/train/img_001.png
images/train/img_002.png  →  masks/train/img_002.png

# Mask pixel values:
# 0 = background (black)
# 1 = class 1 (e.g., tissue)
# 2 = class 2 (e.g., lesion)
# ...

# Use PNG with palette mode for multi-class`;

    case 'medmnist':
      return `# NPZ file structure (NumPy archive)
import numpy as np

data = np.load('pathmnist.npz')

# Available keys:
train_images = data['train_images']  # (89996, 28, 28, 3)
train_labels = data['train_labels']  # (89996,)
val_images = data['val_images']      # (10004, 28, 28, 3)
val_labels = data['val_labels']      # (10004,)
test_images = data['test_images']    # (7180, 28, 28, 3)
test_labels = data['test_labels']    # (7180,)

# Labels are integers (0-8 for PathMNIST)
# 0: adipose, 1: background, 2: debris, ...`;
  }
};

// Get labeling tips based on task
const getLabelingTips = (task: MONAITaskType, modality: MONAIModality): string[] => {
  const tips: string[] = [];

  // Task-specific tips
  switch (task) {
    case 'segmentation':
      tips.push('Use consistent label values across all masks (0=background, 1,2,3...=organs/structures)');
      tips.push('Ensure masks have exact same dimensions as source images');
      tips.push('For 3D volumes, label all slices where the structure is visible');
      tips.push('Use smooth boundaries - avoid jagged edges from hasty annotation');
      tips.push('Double-check label continuity between adjacent slices');
      break;
    case 'classification':
      tips.push('Ensure balanced class distribution when possible');
      tips.push('Use multiple annotators and consensus for ambiguous cases');
      tips.push('Document criteria for borderline cases');
      tips.push('Include patient metadata (age, sex) if available for stratified splits');
      break;
    case 'detection':
      tips.push('Use consistent bounding box format (x, y, width, height or x1, y1, x2, y2)');
      tips.push('Label all instances of target objects in each image');
      tips.push('Maintain consistent box tightness around objects');
      tips.push('For 3D detection, specify z-coordinates or slice ranges');
      break;
    case 'registration':
      tips.push('Ensure paired images are from same patient/timepoint');
      tips.push('Include landmark annotations for validation');
      tips.push('Document acquisition parameters for each scan');
      break;
  }

  // Modality-specific tips
  switch (modality) {
    case 'CT':
      tips.push('Consider windowing (lung, soft tissue, bone) for different structures');
      tips.push('HU values matter - verify proper calibration');
      break;
    case 'MRI':
      tips.push('Note the sequence type (T1, T2, FLAIR, DWI) - affects intensity');
      tips.push('Account for intensity inhomogeneity across scanners');
      break;
    case 'Pathology':
      tips.push('Note magnification level (5x, 10x, 20x, 40x)');
      tips.push('Account for staining variations between labs');
      break;
    case 'X-Ray':
      tips.push('Ensure consistent orientation (PA, AP, lateral)');
      tips.push('Note presence of implants or foreign objects');
      break;
  }

  return tips;
};

// Get common mistakes based on format and task
const getCommonMistakes = (format: MONAIDatasetFormat, task: MONAITaskType): string[] => {
  const mistakes: string[] = [];

  // Format-specific mistakes
  switch (format) {
    case 'nifti':
      mistakes.push('Mismatched affine matrices between image and label');
      mistakes.push('Incorrect voxel spacing (check header metadata)');
      mistakes.push('Label values not starting from 0');
      mistakes.push('Compressed files with wrong extension (.nii vs .nii.gz)');
      break;
    case 'dicom':
      mistakes.push('Missing slices in the series');
      mistakes.push('Incorrect slice ordering (verify ImagePositionPatient)');
      mistakes.push('Mixed series with different orientations');
      mistakes.push('Not handling multi-frame DICOM correctly');
      break;
    case 'png':
      mistakes.push('Using JPEG compression for masks (causes interpolation artifacts)');
      mistakes.push('RGB masks instead of single-channel/palette mode');
      mistakes.push('Mismatched filenames between images and masks');
      mistakes.push('Different resolutions between image and mask');
      break;
    case 'medmnist':
      mistakes.push('Not normalizing pixel values to [0, 1]');
      mistakes.push('Forgetting to convert labels to int64');
      mistakes.push('Loading as float instead of uint8');
      break;
  }

  // Task-specific mistakes
  switch (task) {
    case 'segmentation':
      mistakes.push('Overlapping labels for exclusive classes');
      mistakes.push('Missing labels on some slices (holes in 3D)');
      mistakes.push('Inconsistent label assignment across annotators');
      break;
    case 'classification':
      mistakes.push('Data leakage: same patient in train and test sets');
      mistakes.push('Label noise: mislabeled samples affecting model performance');
      mistakes.push('Class imbalance not accounted for during training');
      break;
    case 'detection':
      mistakes.push('Unlabeled objects (false negatives in ground truth)');
      mistakes.push('Inconsistent IoU thresholds during evaluation');
      break;
  }

  return mistakes;
};

const MONAIDatasetCard = ({
  dataset,
  open,
  onOpenChange,
  onSelectDataset,
}: MONAIDatasetCardProps) => {
  if (!dataset) return null;

  const directoryStructure = getDirectoryStructure(dataset.format, dataset.task);
  const annotationExample = getAnnotationExample(dataset.format, dataset.task);
  const labelingTips = getLabelingTips(dataset.task, dataset.modality);
  const commonMistakes = getCommonMistakes(dataset.format, dataset.task);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Database className="h-5 w-5 text-blue-500" />
            {dataset.displayName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Badges */}
          <div className="flex flex-wrap gap-2">
            <Badge className="bg-purple-500/10 text-purple-600 border-purple-500/30 capitalize">
              {dataset.task}
            </Badge>
            <Badge className={getModalityColor(dataset.modality)}>
              {dataset.modality}
            </Badge>
            <Badge variant="outline" className="font-mono">
              {getFormatIcon(dataset.format)}
              <span className="ml-1">{dataset.format.toUpperCase()}</span>
            </Badge>
            <Badge
              variant="outline"
              className={
                dataset.status === 'ready'
                  ? 'bg-green-500/10 text-green-600 border-green-500/30'
                  : 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30'
              }
            >
              {dataset.status}
            </Badge>
          </div>

          {/* Description */}
          <p className="text-muted-foreground">{dataset.description}</p>

          {/* Statistics */}
          <div className="grid grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4 text-center">
                <div className="text-xs text-muted-foreground mb-1">Train</div>
                <div className="text-2xl font-bold">
                  {dataset.trainCount.toLocaleString()}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 text-center">
                <div className="text-xs text-muted-foreground mb-1">Validation</div>
                <div className="text-2xl font-bold">
                  {dataset.valCount.toLocaleString()}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 text-center">
                <div className="text-xs text-muted-foreground mb-1">Test</div>
                <div className="text-2xl font-bold">
                  {dataset.testCount?.toLocaleString() || '-'}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 text-center">
                <div className="text-xs text-muted-foreground mb-1">Size</div>
                <div className="text-2xl font-bold">{dataset.totalSize}</div>
              </CardContent>
            </Card>
          </div>

          {/* Classes */}
          <Card>
            <CardContent className="pt-4">
              <div className="text-sm font-medium mb-2">
                Classes ({dataset.classes.length})
              </div>
              <div className="flex flex-wrap gap-1">
                {dataset.classes.map((cls) => (
                  <Badge key={cls} variant="secondary" className="text-xs">
                    {cls}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Directory Structure */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-3">
                <Folder className="h-4 w-4 text-blue-500" />
                <span className="text-sm font-medium">
                  Directory Structure ({dataset.format.toUpperCase()})
                </span>
              </div>
              <div className="bg-muted/50 rounded-lg p-3">
                <pre className="text-xs font-mono whitespace-pre text-muted-foreground">
                  {directoryStructure.join('\n')}
                </pre>
              </div>
            </CardContent>
          </Card>

          {/* Annotation Example */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="h-4 w-4 text-green-500" />
                <span className="text-sm font-medium">Annotation Example</span>
              </div>
              <div className="bg-muted/50 rounded-lg p-3">
                <pre className="text-xs font-mono whitespace-pre text-muted-foreground overflow-x-auto">
                  {annotationExample}
                </pre>
              </div>
            </CardContent>
          </Card>

          {/* Labeling Guidelines */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-3">
                <Lightbulb className="h-4 w-4 text-yellow-500" />
                <span className="text-sm font-medium">
                  Labeling Guidelines for {dataset.task} ({dataset.modality})
                </span>
              </div>
              <ul className="space-y-2">
                {labelingTips.map((tip, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">{tip}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Common Mistakes to Avoid */}
          <Card className="border-orange-500/30">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="h-4 w-4 text-orange-500" />
                <span className="text-sm font-medium text-orange-700">
                  Common Mistakes to Avoid
                </span>
              </div>
              <ul className="space-y-2">
                {commonMistakes.map((mistake, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm">
                    <XCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">{mistake}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Path Info */}
          <Card className="bg-muted/30">
            <CardContent className="pt-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Dataset Path</span>
                <code className="text-sm font-mono">{dataset.path}</code>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Format</span>
                <span className="text-sm capitalize">{dataset.format}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Total Samples</span>
                <span className="text-sm">
                  {(dataset.trainCount + dataset.valCount + (dataset.testCount || 0)).toLocaleString()}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Demo Notice */}
          <Alert className="border-blue-500/30 bg-blue-500/5">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-700">
              <span className="font-medium">MONAI Format:</span> This dataset follows the
              Medical Decathlon / MONAI standard format. Images and labels should be properly
              aligned with matching spatial metadata.
            </AlertDescription>
          </Alert>

          {/* Actions */}
          {onSelectDataset && (
            <Button
              className="w-full"
              onClick={() => {
                onSelectDataset(dataset);
                onOpenChange(false);
              }}
            >
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Select Dataset for Training
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MONAIDatasetCard;
